import { useDialog } from "@fpsoftware/mvp";
import { type ComponentProps, useEffect, useState } from "react";
import { type Control, type FieldPath, useController } from "react-hook-form";
import type { FieldValues } from "react-hook-form/dist/types/fields";
import { TbPlus, TbUser } from "react-icons/tb";
import type { Borrower } from "shared";
import { useDebounceValue } from "usehooks-ts";
import { EditBorrowerDialog } from "@/borrower/EditBorrowerDialog";
import {
  useBorrowerSearch,
  useBorrowers,
  useSuspenseBorrower,
} from "@/borrower/hooks";
import {
  AutocompleteInput,
  type Option,
} from "@/components/form/AutocompleteInput";

function splitNames(value: string) {
  const normalized = value.trim();

  if (normalized === "") {
    return [null, null];
  }
  // Make the assumption of single last name and multiple given names
  const split = normalized.split(" ");
  const lastName = split.pop();
  const firstName = split.length > 0 ? split.join(" ") : null;

  return [firstName, lastName ?? null];
}

function parseBorrower(input: string): Partial<Borrower> {
  const normalized = input.trim();

  // Attempt to parse `First Last <first.last@example.com>`
  const match = normalized.match(/^(.*)<(.*)>$/);
  if (match) {
    const [_, name, email] = match;
    const [firstName, lastName] = splitNames(name);

    return {
      firstName: firstName ?? "",
      lastName: lastName ?? "",
      email,
    };
  }

  // If it can not be parsed in email format, assume just a name
  const [firstName, lastName] = splitNames(normalized);
  return {
    firstName: firstName ?? "",
    lastName: lastName ?? "",
  };
}

function getLabel(borrower: Borrower) {
  return `${borrower.firstName} ${borrower.lastName} <${borrower.email}>`;
}

interface BorrowerSelectProps<TFieldValues extends FieldValues>
  extends Omit<
    ComponentProps<typeof AutocompleteInput>,
    "onSelect" | "options"
  > {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
}
export function BorrowerSelect<TFieldValues extends FieldValues>(
  props: BorrowerSelectProps<TFieldValues>,
) {
  const { control, name, ...rest } = props;
  const {
    field: { value, onChange, onBlur },
  } = useController({ control, name });
  const dialog = useDialog();
  const selected = useSuspenseBorrower(value as number | null | undefined);
  const [inputValue, setInputValue] = useState("");
  const [debounced] = useDebounceValue(inputValue, 500);
  const { isLoading: searchLoading, data: search } =
    useBorrowerSearch(debounced);
  const { isLoading: borrowersLoading, data: borrowers } = useBorrowers(
    search ?? [],
  );

  const options: Option<"new" | number>[] = [];
  if (inputValue.length >= 1) {
    options.push({ icon: TbPlus, label: "Add New", value: "new" });
  }
  for (const borrower of borrowers) {
    if (!borrower) {
      continue;
    }

    options.push({
      icon: TbUser,
      label: getLabel(borrower),
      value: borrower.ID,
    });
  }

  useEffect(() => {
    if (selected) {
      setInputValue(getLabel(selected));
    }
  }, [selected]);

  const handleChange = (option: Option<"new" | number>) => {
    if (option.value === "new") {
      dialog.open(EditBorrowerDialog, {
        onConfirm: onChange,
        borrower: parseBorrower(inputValue),
      });
      return;
    }

    setInputValue(option.label);
    onChange(option.value);
    onBlur();
  };

  return (
    <AutocompleteInput
      loading={
        inputValue !== "" &&
        (searchLoading || debounced !== inputValue || borrowersLoading)
      }
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      options={options}
      onSelect={handleChange}
      {...rest}
    />
  );
}
